import { useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SessionContext } from "./context";
import { sessionExpiredEvent, setSessionUser, type Session } from "./session";
import { primaryAction, secondaryAction } from "./styles";

export function AuthBoundary({ children }: { children: ReactNode }) {
 const { t } = useTranslation();
 const [session, setSession] = useState<Session | null>(null);
 const [status, setStatus] = useState<"loading" | "ready" | "anonymous" | "unavailable">("loading");
 const [attempt, setAttempt] = useState(0);
 useEffect(() => {
  let stopped = false;
  let identity = "";
  const controller = new AbortController();
  let checking = false;
  async function check() {
   if (checking) return;
   checking = true;
   try {
    const response = await fetch("/auth/session", { credentials: "same-origin", cache: "no-store", signal: controller.signal });
    if (stopped) return;
    if (response.status === 401) { setSessionUser(""); setSession(null); setStatus("anonymous"); return; }
    if (!response.ok) throw new Error("Session unavailable");
    const next = await response.json() as Session;
    if (stopped) return;
    // A full navigation also discards in-flight exports, forms and mutations
    // belonging to the previous account, not just TanStack Query data.
    if (identity && identity !== next.user.id) { window.location.reload(); return; }
    identity = next.user.id;
    setSessionUser(next.user.id); setSession(next); setStatus("ready");
   } catch {
    if (!stopped) { setSessionUser(""); setSession(null); setStatus("unavailable"); }
   } finally { checking = false; }
  }
  const expire = () => { setSessionUser(""); setSession(null); setStatus("anonymous"); };
  const visible = () => { if (document.visibilityState === "visible") void check(); };
  const restored = (event: PageTransitionEvent) => { if (event.persisted) window.location.reload(); };
  void check();
  const timer = window.setInterval(() => void check(), 30_000);
  window.addEventListener("focus", visible);
  window.addEventListener("pageshow", restored);
  document.addEventListener("visibilitychange", visible);
  window.addEventListener(sessionExpiredEvent, expire);
  return () => { stopped = true; controller.abort(); window.clearInterval(timer); window.removeEventListener("focus", visible); window.removeEventListener("pageshow", restored); document.removeEventListener("visibilitychange", visible); window.removeEventListener(sessionExpiredEvent, expire); };
 }, [attempt]);
 if (status === "ready" && session) return <Workspace key={session.user.id} session={session}>{children}</Workspace>;
 const error = new URLSearchParams(window.location.search).get("auth_error");
 return <main className="grid min-h-screen place-items-center bg-canvas px-5 py-10 text-ink">
  <section className="surface-elevated w-full max-w-md rounded-3xl border border-line p-8 sm:p-10" aria-labelledby="sign-in-title">
   <span className="mb-8 grid size-12 place-items-center rounded-2xl bg-ink font-black text-canvas" aria-hidden="true">H+</span>
   <p className="page-eyebrow" translate="no">Health Tracker</p>
   <h1 id="sign-in-title" className="mt-3 text-3xl font-semibold tracking-tight">{t("auth.title")}</h1>
   <p className="mt-4 leading-relaxed text-muted">{t("auth.description")}</p>
   {error ? <><p role="alert" className="mt-5 text-sm text-ink">{t(error === "access_denied" ? "auth.denied" : "auth.failed")}</p><a className={`${secondaryAction} mt-4`} href="/auth/switch-account">{t("auth.switchAccount")}</a></> : null}
   {status === "loading" ? <p role="status" className="mt-7 text-muted">{t("auth.loading")}</p> : status === "unavailable" ? <><p role="alert" className="mt-6 text-sm">{t("auth.unavailable")}</p><button className={`${primaryAction} mt-5`} onClick={() => { setStatus("loading"); setAttempt(a => a + 1); }}>{t("auth.retry")}</button></> : <a className={`${primaryAction} mt-8 w-full`} href="/auth/login">{t("auth.login")}</a>}
  </section>
 </main>;
}

function Workspace({ session, children }: { session: Session; children: ReactNode }) {
 const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } } }));
 useEffect(() => () => { void client.cancelQueries(); client.clear(); }, [client]);
 return <SessionContext value={session}><QueryClientProvider client={client}>{children}</QueryClientProvider></SessionContext>;
}
